package com.zi.support.shared

/**
 * One customer-portal sign-in (portalSessions/{id}). Cloud Functions create it on sign-in;
 * the portal stamps lastSeenAt on opening, leaving and each action, and logoutAt on Log out.
 * Times are epoch millis, 0 when not set.
 */
data class PortalSession(
    val id: String,
    val email: String,
    val companyId: String,
    /** "code" (emailed pin) or "qr" (sign in on your phone). */
    val method: String,
    val loginAt: Long,
    val lastSeenAt: Long,
    val logoutAt: Long,
    val expiresAt: Long,
    val userAgent: String,
    val ticketsOpened: Long,
    val ticketsCreated: Long,
    val updatesPosted: Long,
)

data class PortalUsageSummary(
    val signIns: Int,
    val customers: Int,
    val companies: Int,
    val onlineNow: Int,
    val stillSignedIn: Int,
    /** Average time from sign-in to last seen, over the period's sessions. */
    val averageMillis: Long,
    val ticketsOpened: Long,
    val ticketsCreated: Long,
    val updatesPosted: Long,
)

object PortalUsage {
    const val ONLINE = "Online"
    const val SIGNED_IN = "Signed in"
    const val LOGGED_OUT = "Logged out"
    const val EXPIRED = "Expired"
    /** The portal stamps lastSeenAt on opening, leaving and each action (no timer), so seen within 15 minutes counts as online. */
    const val ONLINE_MILLIS = 15 * 60_000L
    /** Sessions last 30 days, so this far back covers everyone who could still be signed in. */
    const val SESSION_MILLIS = 30L * 24 * 60 * 60_000L
    val periods: List<String> = listOf(DriverLogs.USAGE_DAY, DriverLogs.USAGE_WEEK, DriverLogs.USAGE_MONTH)

    /** [start, end) epoch millis of the Day / Week / Month around [day] ("yyyy-MM-dd", SAST). */
    fun periodMillis(period: String, day: String): Pair<Long, Long> {
        val (first, last) = DriverLogs.usageSpan(period, day)
        return (DriverLogs.dayStart(first) ?: 0L) to ((DriverLogs.dayStart(last) ?: 0L) + 24 * 60 * 60_000L)
    }

    /** Oldest lastSeenAt worth reading: the period's start, or 30 days back for who is still signed in. */
    fun readFrom(periodStart: Long, nowMillis: Long): Long = minOf(periodStart, nowMillis - SESSION_MILLIS)

    fun status(session: PortalSession, nowMillis: Long): String = when {
        session.logoutAt > 0 -> LOGGED_OUT
        session.expiresAt in 1..nowMillis -> EXPIRED
        nowMillis - session.lastSeenAt <= ONLINE_MILLIS -> ONLINE
        else -> SIGNED_IN
    }

    /** Was the portal open at any point in [start, end)? */
    fun activeIn(session: PortalSession, start: Long, end: Long): Boolean =
        session.loginAt < end && maxOf(session.lastSeenAt, session.loginAt) >= start

    fun durationMillis(session: PortalSession): Long =
        (maxOf(session.lastSeenAt, session.logoutAt.takeIf { it > 0 } ?: 0L) - session.loginAt).coerceAtLeast(0)

    /** Sessions in the period, newest activity first. */
    fun inPeriod(sessions: List<PortalSession>, start: Long, end: Long): List<PortalSession> =
        sessions.filter { activeIn(it, start, end) }.sortedByDescending { maxOf(it.lastSeenAt, it.loginAt) }

    fun summary(sessions: List<PortalSession>, start: Long, end: Long, nowMillis: Long): PortalUsageSummary {
        val shown = inPeriod(sessions, start, end)
        return PortalUsageSummary(
            signIns = shown.count { it.loginAt in start until end },
            customers = shown.map { it.email }.distinct().size,
            companies = shown.map { it.companyId }.distinct().size,
            onlineNow = sessions.count { status(it, nowMillis) == ONLINE },
            stillSignedIn = sessions.count { status(it, nowMillis).let { s -> s == ONLINE || s == SIGNED_IN } },
            averageMillis = if (shown.isEmpty()) 0 else shown.sumOf(::durationMillis) / shown.size,
            ticketsOpened = shown.sumOf { it.ticketsOpened },
            ticketsCreated = shown.sumOf { it.ticketsCreated },
            updatesPosted = shown.sumOf { it.updatesPosted },
        )
    }

    /** "45s", "12m", "3h 05m", "2d 4h". */
    fun duration(millis: Long): String {
        val minutes = millis / 60_000
        return when {
            millis < 60_000 -> "${millis / 1000}s"
            minutes < 60 -> "${minutes}m"
            minutes < 24 * 60 -> "${minutes / 60}h ${(minutes % 60).toString().padStart(2, '0')}m"
            else -> "${minutes / (24 * 60)}d ${minutes / 60 % 24}h"
        }
    }

    /** "Chrome on Windows" from a browser user agent. */
    fun device(userAgent: String): String {
        val ua = userAgent.lowercase()
        if (ua.isBlank()) return "Unknown device"
        val browser = when {
            "edg/" in ua -> "Edge"
            "opr/" in ua || "opera" in ua -> "Opera"
            "samsungbrowser" in ua -> "Samsung Internet"
            "firefox" in ua || "fxios" in ua -> "Firefox"
            "chrome" in ua || "crios" in ua -> "Chrome"
            "safari" in ua -> "Safari"
            else -> "Browser"
        }
        val os = when {
            "iphone" in ua -> "iPhone"
            "ipad" in ua -> "iPad"
            "android" in ua -> "Android"
            "windows" in ua -> "Windows"
            "mac os" in ua || "macintosh" in ua -> "Mac"
            "linux" in ua -> "Linux"
            else -> "other"
        }
        return "$browser on $os"
    }

    fun methodLabel(method: String): String = if (method == "qr") "Phone QR" else "Email code"
}
